<template>
<Menu v-slot="{ open }" as="div" class="relative inline-block text-left text-base/6 sm:text-sm/5">
  <MenuButton
    :class="[
      'rounded-md inline-flex w-full justify-between items-center gap-x-1.5 text-slate-500 px-2 py-2.5 sm:py-2 font-medium',
      open ? 'bg-secondary/5 text-white' : 'hover:bg-secondary/5 hover:text-white',
    ]"
  >
    <div class="flex gap-3 items-center">
      <BaseAvatar v-if="currentUser.avatar" :image="currentUser.avatar" class="size-10" />
      <BaseAvatar v-else class="size-10" />
      <span v-if=" currentUser?.name" class="hidden lg:block text-left">{{ currentUser?.name.split(' ')[0] }}</span>
    </div>
    <span class="hidden lg:block i-heroicons-chevron-up-down -mr-1 size-4" aria-hidden="true" />
  </MenuButton>

  <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
    <MenuItems 
      :class="[
        'absolute min-w-56 w-max z-50 rounded-md  shadow-lg ring-1 focus:outline-none',
        { 'left-0 top-20 origin-top-left': position === 'bottom-left' },
        { 'right-0 top-20 origin-top-right': position === 'bottom-right' },
        { 'left-5 bottom-20 origin-bottom-left': position === 'top-left' },
        { 'right-0 bottom-20 origin-bottom-right': position === 'top-right' },
        { 'bg-white ring-slate-300': variant === 'default' },
        { 'bg-secondary/20 backdrop-blur-xl ring-secondary': variant === 'secondary' }
      ]"
    >
      <div class="p-1">
        <MenuItem v-for="menu in menuItems" :key="menu.label">
          <a
            :href="menu.href"
            :class="[
              'lg:text-sm group flex gap-2 items-center rounded-md p-1.5',
              { 'text-black hover:bg-slate-100': variant === 'default' },
              { 'text-slate-500 hover:bg-secondary hover:text-slate-950': variant === 'secondary' },
            ]"
            @click="onItemClicked(menu)"
          >
            <span 
              v-if="menu.iconClass" 
              :class="[menu.iconClass, 'size-6 lg:size-4']"
              aria-hidden="true" 
            />
            <span>{{ menu.label }}</span>
          </a>
        </MenuItem>
      </div>
    </MenuItems>
  </transition>
</Menu>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import { NavItemType } from '@/components/NavItem.vue'
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'

const { auth, currentUser } = useAuth()
const router = useRouter()

defineProps({
  variant: {
    type: String as PropType<'default' | 'primary' | 'secondary'>,
    default: 'default',
  },
  position: {
    type: String as PropType<'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'>,
    default: 'bottom-right',
  },
})

const logout = async () => {
  await auth.logout()
  router.push('/login')
}

const menuItems: NavItemType[] = [
  { name: '', label: 'My account', href: '/user', iconClass: 'i-heroicons-user-circle' },
  { name: '', label: 'Sign out', href: '#', iconClass: 'i-heroicons-arrow-right-start-on-rectangle', command: logout },
]

const onItemClicked = async (item: NavItemType) => {
  if (item.command) {
    await item.command()
  }
}
</script>
